// Icicle partition of the ILO cluster tree, for the cluster Detail
// lens's map. Pure + deterministic, no DOM:
//
//   • GEOMETRY is structure: each node's horizontal span is
//     proportional to its LEAF COUNT, so the map looks identical at
//     every scope — a stable atlas of the taxonomy the eye can learn.
//   • HEAT is the scope: per-node activity (scoped prompt counts)
//     drives fill intensity in the renderer,
//     showing where this scope's traffic lives in the taxonomy.
//
// Depth comes from traversal (root = row 0), not the stored
// HIERARCHY_LEVEL — older generations numbered levels from the leaves
// upward, and the map reads top-down. Children order by leaf count desc then numeric id,
// so the layout never reshuffles between renders or scopes.

/**
 * Use explicit subtree totals when supplied. Distinct prompt counts can
 * overlap across children, so rows marked includesDescendants replace the
 * child sum. Leaf-only callers retain additive rollups. Rows keyed by ids
 * outside the tree are ignored (stale generation, outliers).
 * @param tree   result of buildClusterTree / loadClusterTree
 * @param leafRows [{id, value, includesDescendants?}] from getClusterLeafActivity
 * @returns Map<id, number>
 */
export function rollupActivityMap(tree, leafRows) {
    const acc = new Map();
    if (!tree) return acc;
    const leafValue = new Map();
    const subtreeValue = new Map();
    for (const r of leafRows || []) {
        const id = String(r?.id ?? "");
        if (!id) continue;
        if (r.includesDescendants === true) {
            subtreeValue.set(id, Number(r.value) || 0);
            continue;
        }
        leafValue.set(id, (leafValue.get(id) || 0) + (Number(r.value) || 0));
    }
    // Post-order accumulate, iteratively (trees can be hundreds wide).
    const visit = (id, seen) => {
        if (acc.has(id)) return acc.get(id);
        if (subtreeValue.has(id)) {
            const value = subtreeValue.get(id);
            acc.set(id, value);
            return value;
        }
        if (seen.has(id)) return 0; // cycle guard
        seen.add(id);
        const kids = tree.childrenOf.get(id) ?? [];
        let v = kids.length === 0
            ? (leafValue.get(id) || 0)
            : kids.reduce((sum, k) => sum + visit(k, seen), 0);
        // Defensive: an internal node that ALSO carries direct
        // assignments (shouldn't happen, but cheap to honor).
        if (kids.length > 0 && leafValue.has(id)) v += leafValue.get(id);
        acc.set(id, v);
        return v;
    };
    for (const id of tree.byId.keys()) visit(id, new Set());
    return acc;
}

/**
 * Lay the tree out as icicle bands.
 *
 * Spans follow leaf counts by default. `shareOf(id)` replaces that with a
 * share of the parent's span (0..1) for trees whose sizes are not leaf counts:
 * siblings split their parent's span by share, largest first, and when their
 * shares sum to less than 1 the rest of the parent's span stays empty (mass the
 * parent holds that no child accounts for). Shares summing past 1 are scaled
 * down to fit.
 *
 * @returns {{
 *   bands: {id, name, depth, x, w, y, h, activity, leafCount, parentId}[],
 *   height: number, maxDepth: number, maxActivity: number,
 * }}
 */
export function layoutClusterIcicle(tree, {
    width = 760,
    rowHeight = 30,
    rowGap = 2,
    activity = new Map(),
    shareOf = null,
} = {}) {
    const bands = [];
    if (!tree || tree.byId.size === 0) return { bands, height: 0, maxDepth: 0, maxActivity: 0 };

    // Leaf counts drive spans; memoized post-order with a cycle guard.
    const leafCounts = new Map();
    const countLeaves = (id, seen) => {
        if (leafCounts.has(id)) return leafCounts.get(id);
        if (seen.has(id)) return 0;
        seen.add(id);
        const kids = tree.childrenOf.get(id) ?? [];
        const n = kids.length === 0 ? 1 : kids.reduce((sum, k) => sum + countLeaves(k, seen), 0) || 1;
        leafCounts.set(id, n);
        return n;
    };
    for (const id of tree.byId.keys()) countLeaves(id, new Set());

    const shareCache = new Map();
    const share = (id) => {
        if (!shareCache.has(id)) {
            const s = Number(shareOf(id));
            shareCache.set(id, Number.isFinite(s) && s > 0 ? s : 0);
        }
        return shareCache.get(id);
    };
    const order = (ids) => [...ids].sort((a, b) =>
        (shareOf ? share(b) - share(a) : leafCounts.get(b) - leafCounts.get(a))
        || (Number(a) - Number(b))
        || String(a).localeCompare(String(b)));
    // Child spans inside a parent span: by share when given, else by leaf count.
    const spansOf = (ids, span) => {
        if (!shareOf) return ids.map((id) => (leafCounts.get(id) || 1) * px);
        const total = ids.reduce((sum, id) => sum + share(id), 0);
        const scale = total > 1 ? 1 / total : 1;
        return ids.map((id) => span * share(id) * scale);
    };

    const roots = order(tree.roots);
    const totalLeaves = roots.reduce((sum, id) => sum + leafCounts.get(id), 0) || 1;
    const px = width / totalLeaves;

    let maxDepth = 0;
    let maxActivity = 0;
    const emit = (id, x0, span, depth, parentId, seen) => {
        if (seen.has(id)) return;
        seen.add(id);
        const node = tree.byId.get(id);
        if (!node) return;
        const act = activity.get(id) || 0;
        maxDepth = Math.max(maxDepth, depth);
        maxActivity = Math.max(maxActivity, act);
        bands.push({
            id,
            name: node.name,
            depth,
            x: x0,
            w: span,
            y: depth * (rowHeight + rowGap),
            h: rowHeight,
            activity: act,
            leafCount: leafCounts.get(id) || 1,
            parentId,
        });
        let cursor = x0;
        const kids = order(tree.childrenOf.get(id) ?? []);
        const kidSpans = spansOf(kids, span);
        kids.forEach((kid, i) => {
            emit(kid, cursor, kidSpans[i], depth + 1, id, seen);
            cursor += kidSpans[i];
        });
    };
    const seen = new Set();
    let cursor = 0;
    const rootSpans = spansOf(roots, width);
    roots.forEach((rid, i) => {
        emit(rid, cursor, rootSpans[i], 0, null, seen);
        cursor += rootSpans[i];
    });

    return {
        bands,
        height: (maxDepth + 1) * (rowHeight + rowGap) - rowGap,
        maxDepth,
        maxActivity,
    };
}

/**
 * Depth (level count) of the tree, for the map's status line. Band-free
 * traversal so it doesn't depend on a layout pass.
 */
export function treeDepth(tree) {
    if (!tree || tree.byId.size === 0) return 1;
    let max = 1;
    const walk = (id, d, seen) => {
        if (seen.has(id)) return;
        seen.add(id);
        max = Math.max(max, d);
        for (const k of tree.childrenOf.get(id) ?? []) walk(k, d + 1, seen);
    };
    const seen = new Set();
    for (const r of tree.roots) walk(r, 1, seen);
    return max;
}

/**
 * The ancestor chain of `id` (root first, excluding `id` itself).
 * Cycle-guarded; unknown ids return [].
 */
export function ancestorChain(tree, id) {
    const out = [];
    if (!tree) return out;
    const seen = new Set([String(id)]);
    let cur = tree.byId.get(String(id));
    cur = cur && cur.parentId != null ? tree.byId.get(cur.parentId) : null;
    while (cur && !seen.has(cur.id)) {
        out.unshift(cur);
        seen.add(cur.id);
        cur = cur.parentId != null ? tree.byId.get(cur.parentId) : null;
    }
    return out;
}
